/**
 * Deriving what the users page shows.
 *
 * Pure, for the same reason as the device models: this is where the bugs are and
 * where exhaustive testing is cheap.
 *
 * The judgements that matter here are about what an owner is allowed to do to
 * whom. The server enforces all of them — this only avoids offering a button that
 * is going to be refused, which is a courtesy, never a control.
 */

import { relativeTime } from './deviceViewModel.ts';

export type UserRole = 'viewer' | 'installer' | 'operator' | 'owner';

export interface UserRecord {
  id: string;
  email: string;
  role: UserRole;
  last_login_at: string | null;
  created_at: string;
  pending: boolean;
  invite_expires_at: string | null;
  access_scope?: 'all' | 'selected';
  site_ids?: string[];
  excluded_camera_ids?: string[];
}

export type InviteState = 'active' | 'pending' | 'expired';

export interface UserRowModel {
  id: string;
  email: string;
  role: UserRole;
  roleLabel: string;
  /** True for the signed-in operator's own row. */
  isSelf: boolean;
  inviteState: InviteState;
  /** e.g. "Last signed in 3 days ago", or what is true instead. */
  activityLabel: string;
  /** False when acting would strand the tenant, or be acting on yourself. */
  canChangeRole: boolean;
  canRemove: boolean;
  canReinvite: boolean;
  /** Why an action is unavailable. Null when everything is offered. */
  restriction: string | null;
  /** What this person can see, in one phrase. */
  accessLabel: string;
  /** False for owners, who see everything by construction. */
  canScope: boolean;
}

export interface UsersViewModel {
  users: UserRowModel[];
  ownerCount: number;
  pendingCount: number;
  /** True when someone should be told an invitation has lapsed unused. */
  hasExpiredInvites: boolean;
}

export const ROLE_LABEL: Record<UserRole, string> = {
  viewer: 'Viewer',
  installer: 'Installer',
  operator: 'Operator',
  owner: 'Owner',
};

/**
 * Describes what a person can see.
 *
 * Says "every site" plainly rather than leaving it blank. A blank would read as
 * "nothing configured yet" when it actually means the opposite, and that is the
 * misreading that leaves someone with access nobody intended.
 */
export function accessLabelFor(user: UserRecord, siteCount: number): string {
  if (user.role === 'owner') return 'Every site — owners administer the tenant';
  if ((user.access_scope ?? 'all') === 'all') {
    return siteCount === 1 ? 'Every site (1)' : `Every site (${siteCount})`;
  }

  const sites = user.site_ids?.length ?? 0;
  const excluded = user.excluded_camera_ids?.length ?? 0;

  if (sites === 0) return 'No sites — this person sees nothing';
  const base = sites === 1 ? '1 site' : `${sites} sites`;
  return excluded === 0 ? base : `${base}, ${excluded} camera${excluded === 1 ? '' : 's'} hidden`;
}

/** What each role can actually do, in one sentence, shown beside the choice. */
export const ROLE_DESCRIPTION: Record<UserRole, string> = {
  viewer: 'Can see devices, sites and cameras. Changes nothing.',
  installer:
    'Commissions sites — add sites, devices and cameras, scan, set camera passwords. Cannot delete anything.',
  operator: 'Everything an installer can do, plus deleting sites, devices and cameras.',
  owner: 'Everything an operator can do, plus managing people and entitlements.',
};

function parseTime(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  // Unparseable is treated as absent rather than rendering "NaN days ago".
  return Number.isNaN(parsed) ? null : parsed;
}

function inviteStateOf(user: UserRecord, now: number): InviteState {
  if (!user.pending) return 'active';
  const expires = parseTime(user.invite_expires_at);
  // A pending account with no expiry on record is treated as expired: it cannot
  // be redeemed, and showing it as merely "pending" would have someone waiting.
  if (expires === null) return 'expired';
  return expires > now ? 'pending' : 'expired';
}

function activityLabelFor(user: UserRecord, state: InviteState, now: number): string {
  if (state === 'expired') return 'Invitation expired — send a new one';
  if (state === 'pending') {
    const expires = parseTime(user.invite_expires_at) as number;
    const hours = Math.max(0, Math.round((expires - now) / 3_600_000));
    return hours < 1 ? 'Invitation expires within the hour' : `Invitation expires in ${hours} hours`;
  }

  const lastLogin = parseTime(user.last_login_at);
  // Distinct from "never invited": this account has a password and has simply
  // not been used, which is worth seeing when reviewing who still needs access.
  if (lastLogin === null) return 'Has a password but has never signed in';
  return `Last signed in ${relativeTime(lastLogin, now)}`;
}

/**
 * Builds the users list.
 *
 * `selfId` is the signed-in user. Both self-action guards depend on knowing it,
 * and getting it wrong offers buttons that the server will refuse.
 */
export function usersViewModel(
  users: UserRecord[],
  selfId: string | null,
  now: number,
  siteCount = 0,
): UsersViewModel {
  const ownerCount = users.filter((user) => user.role === 'owner').length;

  const rows = users.map((user): UserRowModel => {
    const isSelf = selfId !== null && user.id === selfId;
    const inviteState = inviteStateOf(user, now);

    /*
     * The last owner cannot be demoted or removed, and you cannot act on
     * yourself. These are separate rules: an owner in a two-owner tenant could
     * otherwise demote themselves out of a tenant that still has an owner.
     */
    const isLastOwner = user.role === 'owner' && ownerCount === 1;

    let restriction: string | null = null;
    if (isSelf) {
      restriction = 'You cannot change or remove your own account.';
    } else if (isLastOwner) {
      restriction = 'This is the only owner. Promote someone else first.';
    }

    return {
      id: user.id,
      email: user.email,
      role: user.role,
      roleLabel: ROLE_LABEL[user.role] ?? user.role,
      isSelf,
      inviteState,
      activityLabel: activityLabelFor(user, inviteState, now),
      canChangeRole: !isSelf && !isLastOwner,
      canRemove: !isSelf && !isLastOwner,
      // Only for an account that has never set a password. A reissued invitation
      // for someone active could never be redeemed.
      canReinvite: inviteState !== 'active',
      restriction,
      accessLabel: accessLabelFor(user, siteCount),
      // An owner who could not see a site could not manage it, and could grant
      // themselves access anyway.
      canScope: user.role !== 'owner',
    };
  });

  return {
    users: rows,
    ownerCount,
    pendingCount: rows.filter((row) => row.inviteState === 'pending').length,
    hasExpiredInvites: rows.some((row) => row.inviteState === 'expired'),
  };
}

/** Reads the signed-in user's id out of a session token, or null. */
export function userIdFromToken(token: string | null): string | null {
  if (!token) return null;
  const body = token.split('.')[1];
  if (!body) return null;

  try {
    /*
     * Decoded, never verified. The browser cannot verify a signature it has no
     * key for, and must not act as though it can — this is used only to decide
     * which row to grey out. Every real check happens on the server.
     */
    const payload = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: unknown;
    };
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}
